import React, { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Box, Spinner, Input } from '@chakra-ui/react';
import { X } from 'lucide-react';
import { useDebounce } from 'use-debounce';
import { useAtom } from 'jotai';
import { hashtagAtom } from './atom';
import Cookies from 'js-cookie';
import { FieldHookForm } from '../hook-form/field-hook-form';

export const GET_HASHTAG =
  `${process.env.REACT_APP_API_GATEWAY_URL}/api/v1/publisher/articles/hashtags` || '';

const HashtagInput = (props) => {
  const {
    description,
    label,
    name,
    fontSizeError,
    isHiddenBottomContent,
    bottomContentProps,
    isHiddenError,
    bottomRightContent,
    isOptional,
    colorDes,
    placeholder,
    ...rest
  } = props;

  const {
    formState: { errors },
    control,
    setValue: setFormValue
  } = useFormContext();

  const [hashtag, setHashtag] = useAtom(hashtagAtom);
  const [inputValue, setInputValue] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  const [debouncedValue] = useDebounce(inputValue, 500);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);

  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const suggestionRefs = useRef([]);

  useEffect(() => {
    setSelectedTags(hashtag);
  }, [hashtag]);

  const handleTagRemove = (tagToRemove) => {
    const newTags = selectedTags.filter((tag) => tag !== tagToRemove);
    setSelectedTags(newTags);
    setHashtag(newTags);
    setFormValue(name, newTags);
  };

  const handleTagAdd = (newTag) => {
    if (!newTag) return;

    // Remove # if present at start
    const cleanTag = newTag.startsWith('#') ? newTag.slice(1) : newTag;

    if (!selectedTags.includes(cleanTag)) {
      const newTags = [...selectedTags, cleanTag];
      setSelectedTags(newTags);
      setHashtag(newTags);
      setFormValue(name, newTags);
    }

    setInputValue('');
    setSuggestions([]);
    setActiveSuggestionIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeSuggestionIndex >= 0 && suggestions.length > 0) {
        handleTagAdd(suggestions[activeSuggestionIndex].name);
      } else if (inputValue) {
        handleTagAdd(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      handleTagRemove(selectedTags[selectedTags.length - 1]);
    } else if (e.key === 'ArrowDown' && suggestions.length > 0) {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp' && suggestions.length > 0) {
      e.preventDefault();
      setActiveSuggestionIndex((prev) => (prev > 0 ? prev - 1 : suggestions.length - 1));
    }
  };

  useEffect(() => {
    const searchHashtags = async () => {
      if (!debouncedValue) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const apiUrl = `${GET_HASHTAG}?page=1&limit=1000&search=${encodeURIComponent(debouncedValue)}`;

        const response = await fetch(apiUrl, {
          headers: {
            Authorization: `Bearer ${Cookies.get('token')}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        setSuggestions(data?.data?.hashtags);
      } catch (error) {
        console.error('Error fetching hashtags:', error);
      } finally {
        setLoading(false);
      }
    };

    searchHashtags();
  }, [debouncedValue]);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <FieldHookForm
          name={name}
          description={description}
          label={label}
          fontSizeError={fontSizeError}
          isHiddenBottomContent={isHiddenBottomContent}
          bottomContentProps={bottomContentProps}
          isHiddenError={isHiddenError}
          isOptional={isOptional}
          bottomRightContent={bottomRightContent}
          colorDes={colorDes}
        >
          <div className="relative">
            <div className="min-h-[56px] py-3 px-3 lg:px-6 bg-light-gray rounded-xl border border-[#D3D3D3] focus-within:border-twygs-green transition-colors">
              <div className="flex flex-wrap gap-2">
                {selectedTags.map((tag, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-1 px-2 py-1 bg-[#E6E7EB] rounded-lg text-sm"
                  >
                    <span className="font-medium">#{tag}</span>
                    <button
                      type="button"
                      onClick={() => handleTagRemove(tag)}
                      className="p-0.5 hover:bg-gray-300 rounded-full transition-colors"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <Input
                  ref={inputRef}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={selectedTags.length === 0 ? placeholder : ''}
                  className=" !border-none !shadow-none !p-0 !h-7 !min-w-[120px] flex-grow !text-sm lg:!text-base"
                  {...rest}
                />
              </div>
            </div>

            {(suggestions.length > 0 || loading) && (
              <div className="absolute w-full mt-1 bg-white rounded-xl border border-[#F2F3F7] shadow-lg max-h-[296px] overflow-y-auto z-10">
                {loading ? (
                  <div className="flex justify-center p-4">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  suggestions.map((suggestion, index) => (
                    <button
                      key={suggestion.id}
                      className={`w-full text-left px-4 py-2 text-sm font-semibold hover:bg-[#E6E7EB] ${
                        index === activeSuggestionIndex ? 'bg-[#E6E7EB]' : ''
                      }`}
                      onClick={() => handleTagAdd(suggestion.name)}
                      ref={(el) => (suggestionRefs.current[index] = el)}
                    >
                      #{suggestion.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
        </FieldHookForm>
      )}
    />
  );
};

export default HashtagInput;
